import type { ActivityPlan } from '@linktivity/ars-neo';
import { isPlanBookableOnDay } from '@link/share/hooks/productPageSelectors';
import { isOpenDatePlan } from '@link/share/utils';
import type { CalendarDay } from './calendarRange';

/*
 * Whether a plan can be booked on a day, judged exactly the way the calendar
 * renders it — so no automatic date or plan move can land on a day the
 * calendar shows as sold out. From share's `useProductPage`, unchanged in
 * behaviour.
 */

/** The calendar as it is on screen. */
export interface CalendarView {
  plans: ActivityPlan[];
  /** The days as the calendar renders them; see applyOptionAvailability. */
  days: CalendarDay[];
  /**
   * Marks the unserved slots of a day `days` did not mark (only the selected
   * dates are). Returns the day unchanged when slots need no judging.
   */
  markSlots: (day: CalendarDay) => CalendarDay;
}

const findDay = (view: CalendarView, date: string) =>
  view.days.find(day => day.date === date);

const findPlan = (view: CalendarView, planId: string) =>
  view.plans.find(plan => plan.id === planId);

export function isPlanBookableOnCalendarDay(
  view: CalendarView,
  plan: ActivityPlan,
  day: CalendarDay | undefined
): boolean {
  if (!day?.date) return false;
  // Marking slots only ever takes stock away, so a day already out of stock
  // skips it — most days, on the scans that sweep the whole calendar.
  if (!isPlanBookableOnDay(plan, day)) return false;
  return isPlanBookableOnDay(plan, view.markSlots(day));
}

/** The plan's first bookable day; '' when it has none. */
export function firstBookableDate(view: CalendarView, planId: string): string {
  const plan = findPlan(view, planId);
  if (!plan) return '';
  return (
    view.days.find(day => isPlanBookableOnCalendarDay(view, plan, day))?.date ??
    ''
  );
}

/**
 * The leftmost dated plan bookable on the day — "left first" for the column
 * layout. undefined when no plan can serve it.
 */
export function firstBookablePlanId(
  view: CalendarView,
  day: CalendarDay | undefined
): string | undefined {
  return view.plans.find(
    plan =>
      !isOpenDatePlan(plan) && isPlanBookableOnCalendarDay(view, plan, day)
  )?.id;
}

/** The column calendar offers a day while any plan can serve it. */
export function isDayBookableForAnyPlan(
  view: CalendarView,
  day: CalendarDay | undefined
): boolean {
  return firstBookablePlanId(view, day) !== undefined;
}

/** The first day any plan can serve, with that leftmost plan. */
export function firstDayForAnyPlan(
  view: CalendarView
): { date: string; planId: string } | undefined {
  for (const day of view.days) {
    const planId = firstBookablePlanId(view, day);
    if (planId && day.date) return { date: day.date, planId };
  }
  return undefined;
}

export function isPlanBookableOnDate(
  view: CalendarView,
  planId: string,
  date: string
): boolean {
  const plan = findPlan(view, planId);
  if (!plan || isOpenDatePlan(plan) || !date) return false;
  return isPlanBookableOnCalendarDay(view, plan, findDay(view, date));
}

/**
 * The plan a column-layout date belongs to: the leftmost one with stock on
 * it, so a date always opens on the same plan however it was reached. ''
 * means "keep the current plan": it is that plan already, no plan has stock,
 * or the current plan books without a date.
 */
export function columnPlanForDate(
  view: CalendarView,
  currentPlanId: string,
  date: string
): string {
  const current = findPlan(view, currentPlanId);
  if (!current || isOpenDatePlan(current)) return '';
  const target = firstBookablePlanId(view, findDay(view, date)) ?? '';
  return target === currentPlanId ? '' : target;
}

/**
 * The date a plan lands on when the visitor switches to it, given the date
 * the switch carries over: that date when the plan can serve it, else the
 * plan's first bookable day. '' leaves the plan's own state alone — nothing
 * bookable, or a plan whose date is not the visitor's to move (lottery and
 * open-date: picked by the page, calendar hidden).
 *
 * With nothing to carry over, the column layout still puts the plan on its
 * first bookable day: its seeded date may since have been ruled out by the
 * options, and the shared calendar would show it sold out.
 */
export function activationDate(
  view: CalendarView,
  planId: string,
  sourceDate: string,
  rules: { isLottery: boolean; isColumn: boolean }
): string {
  if (rules.isLottery) return '';
  const plan = findPlan(view, planId);
  if (!plan || isOpenDatePlan(plan)) return '';
  if (!sourceDate) {
    return rules.isColumn ? firstBookableDate(view, planId) : '';
  }
  return isPlanBookableOnCalendarDay(view, plan, findDay(view, sourceDate))
    ? sourceDate
    : firstBookableDate(view, planId);
}
